import React, { useCallback, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Card, Typography, useThemeValue } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared';
import { convertSizeToNumber, formatDateToPresent } from '@/_shared/helpers';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useWizard, WizardNavigateCallback } from '@/_shared/hooks/useWizard';
import { getUserProfileCall } from '@/core/state/api';
import { getEmployeesMeCall } from '@/profile';

export const EmployeeConfirmData = () => {
  const t = useTranslations('configuration.learning.employeeConfirmData');
  const sharedT = useTranslations();
  const { genericResponsiveValue } = useResponsiveStyle();
  const { setCustomOnNext } = useWizard();
  const { notify } = useNotification();

  const { response: responseGetEmployeesMe } = getEmployeesMeCall.hook();
  const { execute: executeGetUserProfile } = getUserProfileCall.hook();

  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;

  const onSubmit = useCallback(
    (navigate?: WizardNavigateCallback) => {
      executeGetUserProfile({
        successSaga: () => {
          navigate?.();
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [executeGetUserProfile, sharedT],
  );

  useEffect(() => {
    setCustomOnNext(navigate => onSubmit(navigate));
  }, [onSubmit, setCustomOnNext]);

  return (
    <Box
      display='flex'
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      alignItems='flex-start'
      justifyContent='center'
      py={genericResponsiveValue({
        breakPoints: { desktop: 'xl' },
        defaultValue: 'm',
      })}
      width='100%'
      style={{ gap: gapXXXL }}
      flex={10}
    >
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 5 },
          defaultValue: 10,
        })}
      >
        <Typography
          variant='h2'
          fontSize={genericResponsiveValue({
            breakPoints: { desktop: 50 as never },
            defaultValue: 'xxxl',
          })}
          lineHeight={genericResponsiveValue({
            breakPoints: { desktop: '60px' as never },
            defaultValue: 'xxxl',
          })}
          fontWeight='bold'
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'm' },
            defaultValue: 'xs',
          })}
        >
          {t('title')}
        </Typography>
        <Typography variant='h4' fontWeight={200} mb='l' color='primary/text'>
          {t('subtitle')}
        </Typography>
        <AlertBadge label={t('disclaimer')} type='transparent' iconName='lightBulb' />
      </Box>
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 5 },
          defaultValue: 12,
        })}
        width={genericResponsiveValue({
          breakPoints: { desktop: 'auto' },
          defaultValue: '100%',
        })}
      >
        <Card
          width={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: '100%',
          })}
          display={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: 'flex',
          })}
          flexDirection={genericResponsiveValue({
            breakPoints: { desktop: 'row' },
            defaultValue: 'column',
          })}
        >
          <Typography
            variant='h1'
            color='primary/text'
            fontSize={34 as never}
            lineHeight={'40px' as never}
            fontWeight='bold'
            mb='m'
          >
            {`${responseGetEmployeesMe?.firstName} ${responseGetEmployeesMe?.lastName}`}
          </Typography>
          <Box display='flex' flexDirection='column'>
            <Box display='grid' gridTemplateColumns='1fr' mb='xs'>
              <Typography variant='p2' color='grey/light'>
                {t('profile.role')}
              </Typography>
              <Typography variant='p1' color='primary/text'>
                {responseGetEmployeesMe?.role || '-'}
              </Typography>
            </Box>
            <Box
              display='grid'
              gridTemplateColumns={genericResponsiveValue({
                breakPoints: { desktop: '1fr 1fr' },
                defaultValue: '1fr',
              })}
              gridColumnGap='xs'
              mb='xs'
            >
              <Box>
                <Typography variant='p2' color='grey/light'>
                  {t('profile.department')}
                </Typography>
                <Typography variant='p1' color='primary/text'>
                  {responseGetEmployeesMe?.department?.name?.toLowerCase() === 'unknown'
                    ? '-'
                    : responseGetEmployeesMe?.department?.name || '-'}
                </Typography>
              </Box>
              <Box
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Typography variant='p2' color='grey/light'>
                  {t('profile.manager')}
                </Typography>
                <Typography variant='p1' color='primary/text'>
                  {responseGetEmployeesMe?.manager?.email || '-'}
                </Typography>
              </Box>
            </Box>
            <Box
              display='grid'
              gridTemplateColumns={genericResponsiveValue({
                breakPoints: { desktop: '1fr 1fr' },
                defaultValue: '1fr',
              })}
              gridColumnGap='xs'
            >
              <Box>
                <Typography variant='p2' color='grey/light'>
                  {t('profile.email')}
                </Typography>
                <Typography variant='p1' color='primary/text'>
                  {responseGetEmployeesMe?.email || '-'}
                </Typography>
              </Box>
              <Box
                mt={genericResponsiveValue({
                  breakPoints: { desktop: 'none' },
                  defaultValue: 'xs',
                })}
              >
                <Typography variant='p2' color='grey/light'>
                  {t('profile.birthDate')}
                </Typography>
                <Typography variant='p1' color='primary/text'>
                  {responseGetEmployeesMe?.birthDate
                    ? formatDateToPresent(responseGetEmployeesMe.birthDate)
                    : '-'}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
